/**
 * Firestore persistence operations for creating and updating facilities.
 */

import { serverTimestamp } from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import type { BatchCreateOperation } from "../../../../shared/api/firebase/firestoreService";
import {
  batchCreateDocuments,
  FirestoreService,
} from "../../../../shared/api/firebase/firestoreService";
import { FirestoreCollectionReference } from "../../../../shared/api/firebase/references";
import { HealthCareFacilityModel } from "../../../data-model/healthFacility";
import { prepareFacilityDataForPersistence } from "./clinicalTrialsFacilitySettings";

/** Input needed to create a healthcare facility and optional tenant root. */
interface AddFacilityParams {
  facilityData: HealthCareFacilityModel;
  serviceTypesInput: string;
  userEmail?: string | null;
}

/**
 * Parses a comma-separated string into a trimmed, non-empty array of service types.
 *
 * @param input - Raw comma-separated service types entered by the user
 * @returns Array of trimmed, non-empty service type strings
 */
const parseServiceTypes = (input: string): string[] =>
  input
    .split(",")
    .map((serviceType) => serviceType.trim())
    .filter((serviceType) => serviceType.length > 0);

/**
 * Adds a facility and, for Clinical Trials, its tenant root in one create-only transaction.
 *
 * @param params - Facility form values, service types, and audit actor
 * @returns Generated ID shared by the healthcare facility and tenant root
 * @throws Error when either generated document already exists or Firestore rejects the transaction
 */
export const addFacilityToDb = async (
  params: AddFacilityParams,
): Promise<string> => {
  const { facilityData, serviceTypesInput, userEmail } = params;
  const newId = uuidv4();
  const timestamp = serverTimestamp();
  const dashboardType = facilityData.dashboard_type ?? "med_onc";
  const persistedFacilityData = prepareFacilityDataForPersistence(facilityData);
  const facilityDocument = {
    ...persistedFacilityData,
    DocId: newId,
    dashboard_type: dashboardType,
    is_external_organization:
      dashboardType === "clinical_trials"
        ? true
        : persistedFacilityData.is_external_organization,
    ...(dashboardType === "clinical_trials" ? { is_active: true } : {}),
    service_types: parseServiceTypes(serviceTypesInput),
    updated_by: userEmail ?? null,
    updated_at: timestamp,
  };

  const operations: BatchCreateOperation[] = [
    {
      collectionPath: FirestoreCollectionReference.healthcareFacility(),
      docId: newId,
      data: facilityDocument,
    },
  ];
  if (dashboardType === "clinical_trials") {
    operations.push({
      collectionPath: FirestoreCollectionReference.clinicalTrials(),
      docId: newId,
      data: {
        facility_id: newId,
        schema_version: 1,
        status: "active",
        created_at: timestamp,
        updated_at: timestamp,
      },
    });
  }

  await batchCreateDocuments(operations);
  return newId;
};

/** Input needed to update an existing healthcare facility. */
interface UpdateFacilityParams {
  facilityId: string;
  existingData: HealthCareFacilityModel | null;
  facilityData: HealthCareFacilityModel;
  selectedAuthStatusFilter: string[];
  serviceTypesInput: string;
  userEmail?: string | null;
}

/**
 * Updates an existing facility in the database.
 *
 * @param params - Existing and edited facility values plus audit metadata
 * @returns Promise resolved after the facility update is committed
 */
export const updateFacilityInDb = async (
  params: UpdateFacilityParams,
): Promise<void> => {
  const {
    facilityId,
    existingData,
    facilityData,
    selectedAuthStatusFilter,
    serviceTypesInput,
    userEmail,
  } = params;
  const persistedFacilityData = prepareFacilityDataForPersistence(facilityData);

  await FirestoreService.updateDocument(
    FirestoreCollectionReference.healthcareFacility(),
    facilityId,
    {
      ...existingData,
      ...persistedFacilityData,
      auth_status_filter: selectedAuthStatusFilter,
      service_types: parseServiceTypes(serviceTypesInput),
      updated_by: userEmail,
      updated_at: new Date().toISOString(),
    },
  );
};
